import { useEffect, useRef } from 'react';
import { focusManager, useQueryClient } from '@tanstack/react-query';
import { APP_IDLE_FORCE_REFRESH_MS } from '../constants/appForeground.constants';
import { shouldForceRefreshAfterIdle } from '../helpers/appForegroundRefresh.helper';

/**
 * After the app was backgrounded for APP_IDLE_FORCE_REFRESH_MS, force-refetch active
 * queries — including hooks that set refetchOnWindowFocus: false (World Cup, etc.).
 */
export function useAppIdleRefresh(enabled: boolean): void {
  const queryClient = useQueryClient();
  const lastBackgroundAtRef = useRef<number | null>(null);
  const queryClientRef = useRef(queryClient);

  queryClientRef.current = queryClient;

  useEffect(() => {
    if (!enabled) return;

    return focusManager.subscribe((focused) => {
      if (!focused) {
        lastBackgroundAtRef.current = Date.now();
        return;
      }

      const now = Date.now();
      if (
        !shouldForceRefreshAfterIdle(
          lastBackgroundAtRef.current,
          now,
          APP_IDLE_FORCE_REFRESH_MS,
        )
      ) {
        return;
      }

      void queryClientRef.current.refetchQueries({ type: 'active' });
    });
  }, [enabled]);
}
